import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { servicesAPI, adminSettingsAPI } from '../services/api';
import { Service, Category, Provider } from '../types';
import { Star, Clock, MapPin, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  
  // Driver-specific states
  const [drivers, setDrivers] = useState<Provider[]>([]);
  const [locationError, setLocationError] = useState('');
  const [cities, setCities] = useState<string[]>([]);

  // Navigation guard: Prevent providers with pending/rejected/not submitted documents from accessing home
  // Wait for auth to finish loading before checking
  useEffect(() => {
    // Don't check if auth is still loading
    if (authLoading) {
      return;
    }
    
    if (user?.role === 'provider') {
      const policeVerificationStatus = user.policeVerificationStatus || 'not_submitted';
      const kycStatus = user.kycStatus || 'not_submitted';

      // Redirect if documents are missing
      if (policeVerificationStatus === 'not_submitted') {
        navigate('/complete-profile', { replace: true });
        return;
      }
      if (policeVerificationStatus === 'approved' && kycStatus === 'not_submitted') {
        navigate('/kyc', { replace: true });
        return;
      }

      // Redirect if documents are rejected (check rejected BEFORE pending)
      if (policeVerificationStatus === 'rejected') {
        navigate('/complete-profile', { replace: true });
        return;
      }
      // If KYC is rejected, allow re-upload even if police verification is pending
      if (kycStatus === 'rejected') {
        navigate('/kyc', { replace: true });
        return;
      }

      // Redirect if documents are pending (only if not rejected)
      if (policeVerificationStatus === 'pending' || kycStatus === 'pending') {
        navigate('/review-pending', { replace: true });
        return;
      }
    }
  }, [user, navigate]);

  // Fetch categories from backend on mount
  // Categories are now hardcoded - no fetch needed

  useEffect(() => {
    if (selectedCategory && selectedCategory !== 'driver') {
      fetchServices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  // Hardcode driver category - no API call needed
  useEffect(() => {
    const hardcodedCategories = [
      { id: 'driver', name: 'Driver', icon: '🚗', slug: 'driver', description: 'Professional driver services' }
    ];
    setCategories(hardcodedCategories);
  }, []);

  // Fetch cities for SEO content
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await adminSettingsAPI.getCitiesAndCoverage();
        setCities(response.data.cities || []);
      } catch (error) {
        console.error('Error fetching cities:', error);
        // Continue with empty cities array - will use fallback message
      }
    };
    fetchCities();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await servicesAPI.getServices({
        category: selectedCategory || undefined,
        limit: 12,
      });
      setServices(response.data.services);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = async (categorySlug: string) => {
    if (selectedCategory === categorySlug) {
      // Deselect category
      setSelectedCategory('');
      setDrivers([]);
      setServices([]);
      return;
    }

    // Special handling for driver category - navigate immediately without setting state
    if (categorySlug === 'driver') {
      // Redirect to driver booking page for authenticated users (customer, provider, or admin)
      if (isAuthenticated) {
        navigate('/book-driver');
      } else {
        navigate('/login');
      }
      return; // Don't set state or proceed further
    }

    // For other categories, set state and fetch services
    setSelectedCategory(categorySlug);
    setDrivers([]);
    setServices([]);
    setLocationError('');
    fetchServices();
  };

  const requestLocation = async () => {
    console.log('🔄 Retrying location request...');
    setLocationError('');
    setSelectedCategory('driver');
    await handleCategoryClick('driver');
  };

  // Add detailed logging for state changes
  useEffect(() => {
    console.log('🔄 State changed - drivers:', drivers.length, 'error:', locationError);
  }, [drivers, locationError]);


  return (
    <div className="bg-white min-h-screen">

      {/* Hero Section - Mobile Responsive */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-tight text-black px-3 sm:px-4 md:px-6 lg:px-8 break-words">
              Hire professional drivers for personal & family travel
            </h1>
            <p className="mt-4 md:mt-5 lg:mt-6 text-base md:text-lg lg:text-xl text-gray-700 px-3 sm:px-4 md:px-6 lg:px-8 leading-relaxed break-words text-left">
              {cities.length > 0 
                ? `AllAvail is a trusted driver service platform offering verified and professional drivers for personal cars, family travel, elderly passengers, and special occasions in ${cities.join(', ')}. If you are looking to hire a driver for hourly, daily, or multi-day trips, AllAvail connects you with experienced drivers near you, ensuring safety, reliability, and transparent pricing.`
                : 'AllAvail is a trusted driver service platform offering verified and professional drivers for personal cars, family travel, elderly passengers, and special occasions. Our driver service is available in major cities throughout the region. If you are looking to hire a driver for hourly, daily, or multi-day trips, AllAvail connects you with experienced drivers near you, ensuring safety, reliability, and transparent pricing.'
              }
            </p>
            {/* Book Driver Button */}
            <div className="mt-6 md:mt-8 lg:mt-10 flex justify-center">
              <button
                onClick={() => {
                  if (isAuthenticated) {
                    navigate('/book-driver');
                  } else {
                    navigate('/login');
                  }
                }}
                className="bg-black text-white px-8 py-3 md:px-10 md:py-4 lg:px-12 lg:py-5 rounded-lg font-bold text-lg md:text-xl lg:text-2xl hover:bg-gray-800 transition-colors shadow-lg"
              >
                {t('home.bookDriver')}
              </button>
            </div>
          </div>
        </div>
        {/* Hero Image - Aligned with header and footer margins */}
        <div className="mt-8 md:mt-10 lg:mt-12 max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <img
            src="/hero-car-image.png"
            alt="Professional driver with elderly passengers in car"
            className="w-full h-auto rounded-lg shadow-lg"
            style={{ maxHeight: '500px', objectFit: 'cover' }}
            onError={(e) => {
              // Fallback to a placeholder if image fails to load
              (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="400"%3E%3Crect fill="%23f3f4f6" width="800" height="400"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="18" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EProfessional Driver Service%3C/text%3E%3C/svg%3E';
            }}
          />
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="bg-white py-8 md:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none break-words">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-black mb-4 md:mb-6">{t('home.aboutTitle')}</h2>
            <p className="text-base md:text-lg lg:text-xl text-gray-700 leading-relaxed mb-4 md:mb-6">
              {cities.length > 0
                ? `AllAvail provides professional driver services for individuals and families who need reliable transportation in ${cities.join(', ')}. Whether you need a driver for a day trip, multi-day journey, or special occasions, our experienced and verified drivers are ready to serve you.`
                : 'AllAvail provides professional driver services for individuals and families who need reliable transportation. Our driver service is available in major cities across the region. Whether you need a driver for a day trip, multi-day journey, or special occasions, our experienced and verified drivers are ready to serve you.'
              }
            </p>
            <p className="text-base md:text-lg lg:text-xl text-gray-700 leading-relaxed mb-6 md:mb-8">
              {t('home.aboutText2')}
            </p>

            {/* Driver Services Section */}
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-black mt-8 md:mt-10 mb-4 md:mb-6">
              Driver Services Offered by AllAvail
            </h2>
            <p className="text-base md:text-lg lg:text-xl text-gray-700 leading-relaxed mb-4 md:mb-6">
              {cities.length > 0
                ? `AllAvail offers comprehensive driver services in ${cities.join(', ')} and surrounding areas. Our professional driver service includes:`
                : 'AllAvail offers comprehensive driver services covering most cities across the region. Our professional driver service includes:'
              }
            </p>
            <ul className="list-disc list-inside text-base md:text-lg text-gray-700 space-y-3 md:space-y-4 mb-6 md:mb-8">
              <li>Personal car drivers for daily commuting and errands</li>
              <li>Drivers for elderly passengers with special care and attention</li>
              <li>Temporary drivers for short-term needs</li>
              <li>Outstation drivers for long-distance travel</li>
              <li>Night-time drivers for late-night travel requirements</li>
              <li>Daily & monthly driver service for extended periods</li>
            </ul>

            <h3 className="text-xl md:text-2xl lg:text-3xl font-semibold text-black mt-6 md:mt-8 mb-4 md:mb-6">{t('home.whyChooseTitle')}</h3>
            <ul className="list-disc list-inside text-base md:text-lg text-gray-700 space-y-3 md:space-y-4 mb-6 md:mb-8">
              <li>{t('home.whyChoose1')}</li>
              <li>{t('home.whyChoose2')}</li>
              <li>{t('home.whyChoose3')}</li>
              <li>{t('home.whyChoose4')}</li>
              <li>{t('home.whyChoose5')}</li>
            </ul>

            {/* FAQ Link Section */}
            <div className="bg-gray-50 rounded-lg p-6 md:p-8 mt-8 md:mt-10 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
                Have Questions?
              </h2>
              <p className="text-base md:text-lg text-gray-700 mb-6">
                Visit our Frequently Asked Questions page to learn more about our driver services, booking process, and policies.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link
                  to="/faq"
                  className="inline-block bg-black text-white px-6 py-3 md:px-8 md:py-4 rounded-lg font-semibold text-base md:text-lg hover:bg-gray-800 transition-colors"
                >
                  View FAQ
                </Link>
                <button
                  onClick={() => {
                    if (isAuthenticated) {
                      navigate('/profile');
                    } else {
                      navigate('/login');
                    }
                  }}
                  className="inline-block bg-white border-2 border-black text-black px-6 py-3 md:px-8 md:py-4 rounded-lg font-semibold text-base md:text-lg hover:bg-gray-100 transition-colors"
                >
                  Contact Support
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Drivers Section - Mobile Responsive */}
      {selectedCategory === 'driver' && (
        <section className="py-8 md:py-12 lg:py-16 bg-white">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
            <h3 className="text-xl md:text-2xl font-bold text-black mb-4 md:mb-8">{t('home.availableDrivers')}</h3>
            
            {locationError && (
              <div className="text-center py-8 md:py-12">
                <AlertCircle className="h-10 md:h-12 w-10 md:w-12 text-red-600 mx-auto mb-3 md:mb-4" />
                <p className="text-sm md:text-base text-red-600 mb-3 md:mb-4 px-4">{locationError}</p>
                <button
                  onClick={requestLocation}
                  className="px-4 md:px-6 py-2 md:py-3 text-sm md:text-base bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
                >
                  {t('home.enableLocation')}
                </button>
              </div>
            )}

            {!locationError && drivers.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {drivers.map((driver) => (
                  <div key={driver._id || driver.id} className="bg-white border border-gray-200 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="p-4 md:p-6">
                      <div className="flex items-center mb-3 md:mb-4">
                        {driver.avatar ? (
                          <img src={driver.avatar} alt={driver.name} className="h-12 w-12 md:h-16 md:w-16 rounded-full object-cover mr-3 md:mr-4 flex-shrink-0" />
                        ) : (
                          <div className="h-12 w-12 md:h-16 md:w-16 rounded-full bg-gray-100 flex items-center justify-center mr-3 md:mr-4 flex-shrink-0">
                            <span className="text-black font-bold text-lg md:text-xl">{driver.name.charAt(0)}</span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="text-base md:text-lg font-semibold text-black truncate">{driver.name}</h4>
                          <div className="flex items-center text-xs md:text-sm">
                            <Star className="h-3 w-3 md:h-4 md:w-4 text-yellow-400 fill-current mr-1 flex-shrink-0" />
                            <span className="text-gray-600 truncate">{driver.rating.toFixed(1)} ({driver.reviewCount})</span>
                          </div>
                        </div>
                      </div>

                      {driver.bio && (
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{driver.bio}</p>
                      )}

                      <div className="space-y-2 text-sm mb-4">
                        {driver.distance !== undefined && (
                          <div className="flex items-center text-gray-600">
                            <MapPin className="h-4 w-4 mr-2 text-black" />
                            <span className="font-semibold">{typeof driver.distance === 'number' ? driver.distance.toFixed(2) : driver.distance || '0.00'} {t('home.kmAway')}</span>
                          </div>
                        )}
                        {driver.experience && (
                          <div className="flex items-center text-gray-600">
                            <Clock className="h-4 w-4 mr-2" />
                            <span>{driver.experience} {t('home.yearsExperience')}</span>
                          </div>
                        )}
                        {driver.hourlyRate && (
                          <div className="text-gray-600">
                            {t('home.baseRate')}: <span className="font-semibold text-black">₹{typeof driver.hourlyRate === 'number' ? driver.hourlyRate.toFixed(2) : driver.hourlyRate}/hr</span>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <span className={`text-sm font-semibold ${driver.isAvailable ? 'text-green-600' : 'text-red-600'}`}>
                          {driver.isAvailable ? t('home.available') : t('home.busy')}
                        </span>
                        {isAuthenticated && user?.role === 'customer' && (
                          <button
                            onClick={() => navigate(`/book-driver/${driver._id || driver.id}`)}
                            disabled={!driver.isAvailable}
                            className="bg-black text-white px-4 py-2 rounded-md text-sm hover:bg-white hover:text-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {t('home.bookDriver')}
                          </button>
                        )}
                        {!isAuthenticated && (
                          <button
                            onClick={() => navigate('/login')}
                            className="bg-black text-white px-4 py-2 rounded-md text-sm hover:bg-white hover:text-black transition-colors"
                          >
                            {t('home.loginToBook')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!locationError && drivers.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-600">{t('home.noDriversFound')}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Services Grid - Only show when non-driver category is selected */}
      {selectedCategory && selectedCategory !== 'driver' && (
        <section className="py-8 md:py-12 lg:py-16 bg-white">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
            <h3 className="text-2xl font-bold text-black mb-8">{t('home.availableServices')}</h3>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-gray-100 rounded-lg shadow-md p-6 animate-pulse">
                    <div className="h-4 bg-gray-300 rounded w-3/4 mb-4"></div>
                    <div className="h-3 bg-gray-300 rounded w-full mb-2"></div>
                    <div className="h-3 bg-gray-300 rounded w-2/3"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {services.map((service) => (
                  <div key={service._id} className="bg-white border border-gray-200 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-black">{service.name}</h4>
                        <div className="flex items-center">
                          <Star className="h-4 w-4 text-yellow-400 fill-current" />
                          <span className="ml-1 text-sm text-gray-600">{service.rating.toFixed(1)}</span>
                        </div>
                      </div>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{service.description}</p>
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          {service.duration} min
                        </div>
                        <div className="flex items-center">
                          ₹{typeof service.price === 'number' ? service.price.toFixed(2) : service.price}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-sm text-gray-600">
                          <MapPin className="h-4 w-4 mr-1" />
                          {service.provider?.name || (typeof service.providerId === 'string' ? 'Provider' : service.providerId.name)}
                        </div>
                        {isAuthenticated && user?.role === 'customer' && (
                          <Link
                            to={`/book/${service.category}`}
                            className="bg-black text-white px-4 py-2 rounded-md text-sm hover:bg-white hover:text-black transition-colors"
                          >
                            Book Now
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!loading && services.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-600">{t('home.noServicesFound')}</p>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default HomePage;
